import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { startCustomSearchAsync, getCustomSearchStatus } from '../services/api';
import './CustomSearchContext.css';

const CustomSearchContext = createContext(null);

function _friendlySearchError(raw) {
  if (!raw) return 'Search failed. Please try again.';
  const lower = raw.toLowerCase();
  if (lower.includes('timed out') || lower.includes('timeout') || lower.includes('read timeout')) {
    return 'Search timed out. The Pearch API is taking longer than expected — please retry in a few minutes.';
  }
  if (lower.includes('connection') || lower.includes('network') || lower.includes('httpsconnection')) {
    return 'Search failed due to a network error. Please check your connection and try again.';
  }
  return 'Search failed. Please try again.';
}

export function CustomSearchProvider({ children }) {
  const [banner, setBanner] = useState({ visible: false, query: '' });
  const [completeModal, setCompleteModal] = useState(null);
  const timeoutRef = useRef(null);
  const taskIdRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    taskIdRef.current = null;
    setBanner({ visible: false, query: '' });
  }, []);

  const startBackgroundSearch = useCallback(
    async (query, searchParams = null, excludePrevious = false) => {
      const q = query.trim();
      if (!q) return;

      stopPolling();
      const { task_id } = await startCustomSearchAsync(q, searchParams, excludePrevious);
      taskIdRef.current = task_id;
      setBanner({ visible: true, query: q });

      const pollOnce = async () => {
        const tid = taskIdRef.current;
        if (!tid) return;

        try {
          const st = await getCustomSearchStatus(tid);
          if (st.state === 'SUCCESS') {
            stopPolling();
            setCompleteModal({
              matchCount: st.match_count ?? 0,
              sessionId: st.session_id,
              error: null,
            });
            window.dispatchEvent(
              new CustomEvent('customSearchComplete', {
                detail: {
                  sessionId: st.session_id,
                  matchCount: st.match_count ?? 0,
                },
              })
            );
            return;
          }
          if (st.state === 'FAILURE') {
            stopPolling();
            setCompleteModal({
              matchCount: 0,
              sessionId: null,
              error: _friendlySearchError(st.error),
            });
            return;
          }
        } catch (err) {
          stopPolling();
          setCompleteModal({
            matchCount: 0,
            sessionId: null,
            error: _friendlySearchError(err?.message),
          });
          return;
        }

        timeoutRef.current = setTimeout(pollOnce, 2000);
      };

      pollOnce();
    },
    [stopPolling]
  );

  const dismissCompleteModal = useCallback(() => {
    setCompleteModal(null);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const value = {
    startBackgroundSearch,
    banner,
    isSearching: banner.visible,
    completeModal,
    dismissCompleteModal,
  };

  return (
    <CustomSearchContext.Provider value={value}>
      {children}
      {banner.visible && (
        <div className="custom-search-banner" role="status" aria-live="polite">
          <span className="custom-search-banner__spinner" />
          <span className="custom-search-banner__text">
            Searching candidates…
            <span className="custom-search-banner__hint">We will notify you when done.</span>
          </span>
        </div>
      )}
      {completeModal && (
        <div
          className="custom-search-modal-overlay"
          onClick={dismissCompleteModal}
          role="presentation"
        >
          <div
            className="custom-search-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="custom-search-modal-title"
          >
            <div className="custom-search-modal__icon">
              {completeModal.error ? (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#dc2626" />
                </svg>
              ) : (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#059669" />
                </svg>
              )}
            </div>
            <h2 id="custom-search-modal-title" className="custom-search-modal__title">
              {completeModal.error ? 'Search failed' : 'Search complete'}
            </h2>
            {completeModal.error ? (
              <p className="custom-search-modal__body">{completeModal.error}</p>
            ) : (
              <p className="custom-search-modal__body">
                Your search finished successfully.
                <strong className="custom-search-modal__count">
                  {' '}
                  {completeModal.matchCount} candidate{completeModal.matchCount !== 1 ? 's' : ''} matched
                </strong>
                .
              </p>
            )}
            <button
              type="button"
              className="custom-search-modal__btn"
              onClick={dismissCompleteModal}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </CustomSearchContext.Provider>
  );
}

export function useCustomSearch() {
  const ctx = useContext(CustomSearchContext);
  if (!ctx) {
    throw new Error('useCustomSearch must be used within CustomSearchProvider');
  }
  return ctx;
}
